import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { auth } from './firebase';
import { showTicketNotification } from './notifications';
import { TicketNotificationCoalescer } from './notification-coalescer';
import {
  isInAudience,
  renderNotification,
  shouldSuppressNotification,
} from './notification-text';

const COALESCE_WINDOW_MS = 60_000;

export interface TicketEventMessage {
  type:
    | 'ticket.created'
    | 'ticket.updated'
    | 'ticket.comment_added'
    | 'ticket.event_added'
    | 'suggestion.created';
  /** Set for ticket.* events. */
  ticketId?: number;
  /** Set for suggestion.* events. */
  suggestionId?: number;
  actorUserId?: string;
  publishedAt: string;
  payload?: Record<string, unknown>;
}

type Listener = (msg: TicketEventMessage) => void;

class TicketEventBroadcaster {
  private listeners = new Set<Listener>();
  emit(msg: TicketEventMessage) {
    for (const l of this.listeners) {
      try {
        l(msg);
      } catch (err) {
        console.error('event listener threw', err);
      }
    }
  }
  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  }
}

const Context = createContext<TicketEventBroadcaster | null>(null);

/**
 * Opens a single SSE connection per tab (mount inside the authenticated Layout) and
 * fans incoming events out to any `useTicketEvents` listener via React Context.
 *
 * Also fires the desktop notification (`showTicketNotification`) for each event — which
 * is suppressed while the tab is visible, so it pairs cleanly with the in-app ToastQueue.
 */
export function TicketEventsProvider({ children }: { children: ReactNode }) {
  const broadcaster = useRef<TicketEventBroadcaster>(new TicketEventBroadcaster()).current;
  // Mutable holder for the signed-in user's id, populated from the SSE "hello"
  // event on connect. Used to suppress browser notifications for the user's own
  // actions (matches the in-app ToastQueue's behavior).
  const selfUserIdRef = useRef<string | null>(null);
  // Coalescer for browser notifications. Bursts of updates/comments on a single
  // ticket collapse into one summary notification per ~60s window.
  const coalescerRef = useRef<TicketNotificationCoalescer | null>(null);
  if (coalescerRef.current === null) {
    coalescerRef.current = new TicketNotificationCoalescer(
      COALESCE_WINDOW_MS,
      (n, ticketId) => {
        showTicketNotification({
          ticketId: ticketId ?? 0,
          title: n.title,
          body: n.body,
          onClickPath: n.path,
        });
      },
      selfUserIdRef,
    );
  }

  useEffect(() => {
    return () => {
      coalescerRef.current?.dispose();
      coalescerRef.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let es: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    async function connect() {
      if (cancelled) return;
      const user = auth.currentUser;
      if (!user) {
        console.warn('[SSE] no current user — not subscribing');
        return;
      }
      // Force refresh on reconnects so we always send a fresh token (Firebase tokens have a 1h TTL).
      const token = await user.getIdToken(attempt > 0);
      if (cancelled) return;

      // SSE bypasses the Firebase Hosting rewrite — Hosting's CDN closes long-lived
      // connections roughly every 60s. We connect directly to the Cloud Run URL when
      // VITE_API_BASE_URL is set; otherwise fall back to same-origin (e.g. local dev).
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
      const eventSource = new EventSource(
        `${apiBase}/api/events/stream?access_token=${encodeURIComponent(token)}`,
      );
      es = eventSource;

      eventSource.onopen = () => {
        attempt = 0;
        console.log('[SSE] connected');
      };
      eventSource.onerror = () => {
        // Browser will auto-retry while readyState === CONNECTING. Once it gives up
        // (readyState === CLOSED, e.g. after Cloud Run's request timeout fires or
        // the token expires), we reconnect manually with a fresh token.
        if (eventSource.readyState === EventSource.CLOSED && !cancelled) {
          console.warn('[SSE] closed — reconnecting');
          eventSource.close();
          if (es === eventSource) es = null;
          const delay = Math.min(30_000, 1_000 * 2 ** attempt);
          attempt += 1;
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            void connect();
          }, delay);
        }
      };

      const handler = (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data) as TicketEventMessage;
          broadcaster.emit(data);

          // Notification-only filters — the SSE refresh still fans out to the
          // app via broadcaster.emit above so live data stays fresh.
          const self = selfUserIdRef.current;
          if (self && data.actorUserId === self) return; // self-suppress
          if (!isInAudience(data, self)) return; // audience filter
          if (shouldSuppressNotification(data)) return; // bookkeeping suppression

          if (coalescerRef.current?.ingest(data)) return;
          const rendered = renderNotification(data, self);
          showTicketNotification({
            ticketId: data.ticketId ?? 0,
            title: rendered.title,
            body: rendered.body,
            onClickPath: rendered.path,
          });
        } catch {
          /* ignore */
        }
      };

      eventSource.addEventListener('hello', (e: MessageEvent) => {
        console.log('[SSE] hello', e.data);
        try {
          const data = JSON.parse(e.data) as { userId?: string | null };
          selfUserIdRef.current = data.userId ?? null;
        } catch {
          /* ignore malformed hello */
        }
      });
      eventSource.addEventListener('ticket.created', handler);
      eventSource.addEventListener('ticket.updated', handler);
      eventSource.addEventListener('ticket.comment_added', handler);
      eventSource.addEventListener('ticket.event_added', handler);
      eventSource.addEventListener('suggestion.created', handler);
    }

    void connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      es?.close();
    };
  }, [broadcaster]);

  return <Context.Provider value={broadcaster}>{children}</Context.Provider>;
}

/**
 * Subscribe to ticket events. Caller's `onMessage` is wrapped in a ref so the latest
 * version is always invoked without needing to re-subscribe.
 */
export function useTicketEvents(onMessage: (msg: TicketEventMessage) => void) {
  const broadcaster = useContext(Context);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  useEffect(() => {
    if (!broadcaster) return;
    return broadcaster.subscribe((msg) => onMessageRef.current(msg));
  }, [broadcaster]);
}

